import { Archive, CalendarClock, CalendarDays, CalendarRange, CircleDollarSign, FileCheck2, House, Landmark, ListChecks, LogOut, Rocket, Settings, ShieldAlert, Target } from "lucide-react";
import { useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell, type NavGroup } from "@otii/ui/shell";
import { post } from "@otii/ui/api";
import { RELEASE_OPEN, packProgress, forecast, GATE_LABEL, ROLE_LABEL, engineInput, measureAt, monthOf, needsYou, obligationNow, prevMonth } from "@otii/gov-shared";
import { useFinanceModel, useMe, useRegisters } from "../lib/queries";
import { GatePill } from "../features/finance/bits";
import { CalendarLinkDialog } from "./CalendarLinkDialog";

const Count = ({ n, tone, title }: { n: number; tone: "terra" | "plain"; title: string }) =>
  n ? (
    <span title={title} className={tone === "terra" ? "rounded-full bg-terra-deep px-1.5 text-[11px] leading-[18px] font-bold text-paper" : "rounded-full bg-paper/15 px-1.5 text-[11px] leading-[18px] font-bold text-paper/80"}>
      {n}
    </span>
  ) : null;

/**
 * The menu in Maggie's three groups: reading the business, meetings, then
 * registers and records. Screens appear here as they're built.
 */
export function Shell() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [calendar, setCalendar] = useState(false);
  const finance = !!me && me.user.role !== "observer";
  const model = useFinanceModel(finance);
  const registers = useRegisters(finance);
  const gate = useMemo(() => {
    const input = model.data ? engineInput(model.data, "applied") : null;
    return input ? forecast(input).gate : null;
  }, [model.data]);
  const needs = useMemo(() => (me && registers.data ? needsYou(me.user, registers.data).length : 0), [me, registers.data]);
  const openActions = registers.data?.actions.filter((a) => a.status === "open").length ?? 0;
  // Maggie's badge: the board's risks, in the first tier
  const firstTier = registers.data?.riskTiers.find((t) => t.active);
  const tier1 = registers.data?.risks.filter((r) => r.status === "open" && r.tierId === firstTier?.id).length ?? 0;
  // overdue or due soon
  const owed = useMemo(() => {
    const r = registers.data;
    if (!r) return 0;
    return r.obligations.filter((o) => o.active && ["overdue", "due_soon"].includes(obligationNow(o, { company: r.company, yearStartMonth: r.yearStartMonth }, r.today).state)).length;
  }, [registers.data]);
  const openChanges = registers.data?.releases.filter((r) => RELEASE_OPEN.includes(r.status)).length ?? 0;
  // behind target last month
  const behind = useMemo(() => {
    const r = registers.data;
    if (!r) return 0;
    const last = prevMonth(monthOf(r.today));
    return r.measures.filter((d) => d.active && measureAt(d, last, r.measureValues, r.measureTargets, r.measureSettings).status === "behind").length;
  }, [registers.data]);

  // each open pack's sections approved
  const [pack, board] = useMemo(() => {
    const r = registers.data;
    return (["monthly", "board"] as const).map((kind) => {
      const p = r?.packs.filter((x) => x.status === "open" && x.kind === kind).sort((a, b) => a.month.localeCompare(b.month))[0];
      return r && p ? packProgress(p, r.packSections) : null;
    });
  }, [registers.data]);
  const progress = (p: { approved: number; total: number } | null | undefined) =>
    p ? (
      <span title={`${p.approved} of ${p.total} sections approved`} className="num rounded-full bg-paper/15 px-1.5 text-[11px] leading-[18px] font-bold text-paper/80">
        {p.approved}/{p.total}
      </span>
    ) : undefined;

  const groups: NavGroup[] = [];
  if (finance) {
    groups.push({
      label: "Read the business",
      items: [
        { to: "/home", label: "Home", icon: House, badge: <Count n={needs} tone="terra" title={`${needs} need you`} /> },
        { to: "/finance", label: "Finance", icon: CircleDollarSign, badge: gate ? <GatePill state={gate} compact title={`Spend gate: ${GATE_LABEL[gate]}`} /> : undefined },
        { to: "/measures", label: "Measures", icon: Target, badge: <Count n={behind} tone="terra" title={`${behind} behind target last month`} /> },
      ],
    });
    groups.push({
      label: "Meetings",
      items: [
        { to: "/pack", label: "Monthly pack", icon: FileCheck2, badge: progress(pack) },
        { to: "/board", label: "Board pack", icon: Landmark, badge: progress(board) },
        { to: "/calendar", label: "Calendar", icon: CalendarRange },
      ],
    });
    groups.push({
      label: "Registers and records",
      items: [
        { to: "/decisions", label: "Decisions & actions", icon: ListChecks, badge: <Count n={openActions} tone="plain" title={`${openActions} open actions`} /> },
        { to: "/risks", label: "Risks", icon: ShieldAlert, badge: <Count n={tier1} tone="plain" title={`${tier1} ${firstTier?.name ?? "Tier 1"} risks`} /> },
        { to: "/obligations", label: "Obligations", icon: CalendarClock, badge: <Count n={owed} tone="terra" title={`${owed} overdue or due soon`} /> },
        { to: "/releases", label: "Release log", icon: Rocket, badge: <Count n={openChanges} tone="plain" title={`${openChanges} open changes`} /> },
        { to: "/records", label: "Records", icon: Archive },
      ],
    });
  } else if (me)
    groups.push({
      label: "Board and records",
      items: [
        { to: "/calendar", label: "Calendar", icon: CalendarRange },
        { to: "/records", label: "Records", icon: Archive },
      ],
    });

  const signOut = async () => {
    await post("/api/auth/logout");
    qc.clear();
    navigate("/sign-in");
  };

  return (
    <AppShell
      product="Governance"
      tagline="Board & management"
      groups={groups}
      pinned={me?.user.isAdmin ? { to: "/admin", label: "Admin", icon: Settings } : null}
      user={me ? { name: me.user.name, avatarUrl: me.user.avatarUrl, roleLabel: `${ROLE_LABEL[me.user.role]}${me.user.isAdmin ? " · Admin" : ""}` } : null}
      userMenu={[...(finance ? [{ label: "Google Calendar", icon: <CalendarDays />, onSelect: () => setCalendar(true) }] : []), { label: "Sign out", icon: <LogOut />, onSelect: signOut }]}
    >
      <Outlet />
      <CalendarLinkDialog open={calendar} onOpenChange={setCalendar} />
    </AppShell>
  );
}
